import React, { useState } from "react";
import { Sparkles, Loader2, ExternalLink, Copy, BookOpen } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useSeo } from "@/hooks/useSeo";

// FindAMT — "Find Auto MindType" (short: FindAMT).
// Single input box that routes queries to Claude / Wikipedia / Google.
// - "Ask" hits POST /api/findamt/ask which decides mode server-side.
// - Result box shows: LLM answer (markdown-lite), optional Wikipedia snippet card,
//   and a "Open in Google" fallback link.

const MODES = [
  { key: "auto",    label: "Auto",    hint: "let AI decide" },
  { key: "chat",    label: "Chat",    hint: "casual Q&A" },
  { key: "extract", label: "Extract", hint: "summarise / parse" },
  { key: "doc",     label: "Doc",     hint: "letters, resumes" },
  { key: "wiki",    label: "Wiki",    hint: "Wikipedia lookup" },
];

const EXAMPLES = [
  "Write a formal rent-increase letter for my tenant",
  "Who was Rani Lakshmibai?",
  "Extract 5 SEO keywords for a plumbing service in Bengaluru",
  "Explain compound interest in Hindi",
  "Best time to promote a matrimonial listing in Mumbai",
];

export default function FindAMT() {
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("auto");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);  // {answer, mode, wiki, google_url}

  useSeo({
    title: "FindAMT · one-box AI for local search, docs & extraction",
    description: "Ask anything — get answers from Claude, Wikipedia and Google in one box. Draft letters, extract data, summarise pages, translate on the fly.",
    canonical: "/findamt",
  });

  const submit = async (q = query, m = mode) => {
    const text = (q || "").trim();
    if (!text) { toast.error("Type a question first"); return; }
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post("/findamt/ask", { query: text, mode: m });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something broke");
    } finally { setLoading(false); }
  };

  const copyAnswer = () => {
    if (!result?.answer) return;
    navigator.clipboard?.writeText(result.answer).then(
      () => toast.success("Copied"),
      () => toast.error("Copy failed"),
    );
  };

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-8 py-10" data-testid="findamt-page">
      <div className="text-center">
        <div className="inline-flex items-center gap-1.5 bg-gradient-to-br from-[#E05E36] to-[#c94e2a] text-white text-[10px] uppercase tracking-widest font-black rounded-full px-3 py-1">
          <Sparkles size={11} /> FindAMT · Find Auto MindType
        </div>
        <h1 className="heading text-3xl md:text-5xl font-extrabold text-stone-900 mt-3">
          Ask anything.<br />Get one clean answer.
        </h1>
        <p className="text-sm md:text-base text-stone-600 mt-2 max-w-xl mx-auto">
          A single box that pulls the best of Claude, Wikipedia and Google — for local search, doc writing, extraction, translations and more.
        </p>
      </div>

      {/* Composer */}
      <div className="mt-8 bg-white border border-stone-200 rounded-3xl p-4 md:p-5 shadow-sm">
        <Textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
          }}
          placeholder="Share your thought · request a doc · extract something · ask a question…"
          className="min-h-[110px] border-0 focus-visible:ring-0 resize-none text-base"
          data-testid="findamt-input"
        />
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {MODES.map(m => (
            <button
              key={m.key}
              onClick={() => setMode(m.key)}
              className={`text-[11px] uppercase tracking-widest font-black px-3 py-1.5 rounded-full min-h-[36px] ${
                mode === m.key
                  ? "bg-[#E05E36] text-white"
                  : "bg-stone-100 text-stone-700 hover:bg-stone-200"
              }`}
              title={m.hint}
              data-testid={`findamt-mode-${m.key}`}
            >
              {m.label}
            </button>
          ))}
          <Button
            onClick={() => submit()}
            disabled={loading || !query.trim()}
            className="ml-auto rounded-full bg-[#E05E36] hover:bg-[#c94e2a] font-bold min-h-[44px]"
            data-testid="findamt-ask-btn"
          >
            {loading ? <><Loader2 size={14} className="animate-spin mr-1" /> Thinking…</> : <><Sparkles size={14} className="mr-1" /> Ask</>}
          </Button>
        </div>
      </div>

      {/* Examples */}
      {!result && !loading && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="findamt-examples">
          {EXAMPLES.map(ex => (
            <button
              key={ex}
              onClick={() => { setQuery(ex); submit(ex, "auto"); }}
              className="text-xs text-stone-700 border border-stone-200 hover:border-[#E05E36] rounded-full px-3 py-1.5 bg-white min-h-[36px]"
            >
              {ex}
            </button>
          ))}
        </div>
      )}

      {/* Answer */}
      {result && (
        <div className="mt-6 space-y-4" data-testid="findamt-result">
          {result.wiki && (
            <a
              href={result.wiki.url}
              target="_blank" rel="noopener noreferrer"
              className="block bg-white border border-stone-200 rounded-2xl p-4 hover:border-[#E05E36] transition-colors"
              data-testid="findamt-wiki-card"
            >
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-black text-[#8B5E34]">
                <BookOpen size={12} /> Wikipedia · {result.wiki.title}
              </div>
              <div className="mt-2 flex gap-3">
                {result.wiki.image && <img src={result.wiki.image} alt="" loading="lazy" className="w-16 h-16 rounded object-cover" />}
                <div className="text-sm text-stone-700 line-clamp-4">{result.wiki.extract}</div>
              </div>
              <div className="text-xs text-[#E05E36] font-bold mt-2 inline-flex items-center gap-1">Read full article <ExternalLink size={11} /></div>
            </a>
          )}

          {result.answer && (
            <div className="bg-white border border-stone-200 rounded-2xl p-5 md:p-6" data-testid="findamt-answer">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest font-black text-[#2D5A27]">
                  Answer · {result.mode}
                </div>
                <button
                  onClick={copyAnswer}
                  className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-900 min-h-[36px] px-2"
                  data-testid="findamt-copy"
                >
                  <Copy size={12} /> Copy
                </button>
              </div>
              <div className="prose prose-sm max-w-none whitespace-pre-wrap text-stone-800 leading-relaxed">{result.answer}</div>
            </div>
          )}

          <a
            href={result.google_url}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-900 font-semibold"
            data-testid="findamt-google-fallback"
          >
            Not enough? Open in Google <ExternalLink size={11} />
          </a>
        </div>
      )}

      <div className="mt-10 text-center text-[11px] text-stone-400">
        Powered by Emergent LLM key · Claude Sonnet 4.6 · Wikipedia REST API
      </div>
    </div>
  );
}
